export const getHeaderValue = (
  headers: HeadersInit | undefined,
  key: string,
): string | null => {
  if (!headers) return null;
  const target = key.toLowerCase();

  if (headers instanceof Headers) {
    return headers.get(key); // case-insensitive
  }

  if (Array.isArray(headers)) {
    const found = headers.find(([k]) => k.toLowerCase() === target);
    return found ? found[1] : null;
  }

  const rec = headers as Record<string, unknown>;
  for (const [k, v] of Object.entries(rec)) {
    if (k.toLowerCase() === target && typeof v === 'string') return v;
  }
  return null;
};

export const hasHeader = (
  headers: HeadersInit | undefined,
  key: string,
): boolean => getHeaderValue(headers, key) !== null;

/**
 * Returns true if headers include a Content-Type key (any casing).
 */
export const hasContentTypeHeader = (
  headers: HeadersInit | undefined,
): boolean => hasHeader(headers, 'content-type');

/**
 * Merge headers into a plain object (Record<string,string>).
 * `extra` overrides `base` for overlapping keys (case-insensitive).
 */
export const mergeHeaders = (
  base: Record<string, string>,
  extra?: HeadersInit,
): Record<string, string> => {
  const out: Record<string, string> = { ...base };

  if (!extra) return out;

  const set = (k: string, v: string) => {
    // normalise to preserve caller key casing preference if possible
    // but ensure we overwrite any existing key ignoring case
    const existingKey = Object.keys(out).find(
      kk => kk.toLowerCase() === k.toLowerCase(),
    );
    if (existingKey) delete out[existingKey];
    out[k] = v;
  };

  if (extra instanceof Headers) {
    extra.forEach((v, k) => set(k, v));
    return out;
  }

  if (Array.isArray(extra)) {
    for (const [k, v] of extra) set(k, v);
    return out;
  }

  for (const [k, v] of Object.entries(extra as Record<string, unknown>)) {
    if (typeof v === 'string') set(k, v);
  }

  return out;
};
